/**
 * [Insider+] Discourse SSO forum-auth entry — bridges Discourse to the iframe.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { INSIDER_PLUS_BASE_URL } from '../security/config';
import LoaderWrap from '../components/Loader/LoaderWrap';
import LoaderIcon from '../components/Loader/LoaderIcon';
import InsiderPlusIframeShell from '../components/InsiderPlus/InsiderPlusIframeShell';
import { selectHasInsiderPlusSubscription } from '../store/selectors/userSelectors';
import { INSIDER_PLUS_PAGE } from '../constants/pages';
import useInsiderPlusIframe from '../util/hooks/useInsiderPlusIframe';

const InsiderPlusForumAuthPage = () => {
  const hasInsiderPlusSubscription = useSelector(
    selectHasInsiderPlusSubscription,
  );

  // Capture Discourse's sso/sig from the parent URL once at mount, then strip
  // them from window.location in an effect — they should live in the iframe
  // URL, not in the parent's browser history or Referer.
  const [ssoParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { sso: params.get('sso'), sig: params.get('sig') };
  });

  useEffect(() => {
    if (!ssoParams.sso && !ssoParams.sig) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('sso');
    url.searchParams.delete('sig');
    window.history.replaceState({}, '', url.toString());
  }, [ssoParams.sso, ssoParams.sig]);

  const buildIframeSrc = useCallback(() => {
    const iframeParams = new URLSearchParams();
    if (ssoParams.sso) iframeParams.set('sso', ssoParams.sso);
    if (ssoParams.sig) iframeParams.set('sig', ssoParams.sig);
    const query = iframeParams.toString();
    return `${INSIDER_PLUS_BASE_URL}/forum-auth${query ? `?${query}` : ''}`;
  }, [ssoParams.sso, ssoParams.sig]);

  const onNavigateUrl = useCallback((url) => {
    window.location.href = url;
  }, []);

  const {
    iframeRef,
    iframeSrc,
    iframeKey,
    isRecovering,
    authFailed,
    isRetrying,
    handleIframeLoad,
    handleRetry,
  } = useInsiderPlusIframe({ buildIframeSrc, onNavigateUrl });

  if (hasInsiderPlusSubscription === null) {
    return (
      <LoaderWrap>
        <LoaderIcon />
      </LoaderWrap>
    );
  }

  if (hasInsiderPlusSubscription === false) {
    return <Redirect to={INSIDER_PLUS_PAGE} />;
  }

  return (
    <InsiderPlusIframeShell
      iframeRef={iframeRef}
      iframeSrc={iframeSrc}
      iframeKey={iframeKey}
      title="Insider Plus Forum Auth"
      dataCy="container-insider-plus-forum-auth-page"
      isRecovering={isRecovering}
      authFailed={authFailed}
      isRetrying={isRetrying}
      onIframeLoad={handleIframeLoad}
      onRetry={handleRetry}
    />
  );
};

export default InsiderPlusForumAuthPage;
